import React, { useEffect } from 'react';
import Services from '../components/Services';
import Faq from '../components/Faq';
import { User, Users, Wifi, Home, MapPin, Zap, Search, Shield, CheckCircle, MessageSquare, Clock, ArrowRight, BadgeCheck, Route, Wrench, ShieldCheck, ServerCog, PhoneCall } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const DepannageInformatique: React.FC = () => {
  const location = useLocation();
  const whatsappUrl = 'https://wa.me/33626748299';

  useEffect(() => {
    document.title = 'Dépannage Informatique à Distance | SOS IT Services';
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Dépannage informatique professionnel à distance : PC lent, virus, panne réseau, problème Windows/Mac. Intervention rapide, satisfait ou remboursé. Diagnostic gratuit pour particuliers et entreprises.');
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://sos-it-services.fr/depannage-informatique');
    // OG tags
    const ogTags: Record<string, string> = {
      'og:title': 'Dépannage Informatique à Distance | SOS IT Services',
      'og:description': 'PC lent, virus, panne réseau — nos experts interviennent à distance rapidement. Satisfait ou remboursé. Diagnostic gratuit.',
      'og:url': 'https://sos-it-services.fr/depannage-informatique',
      'og:image': 'https://sos-it-services.fr/images/depan-hero.png',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    window.scrollTo(0, 0);

    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const howItWorksSteps = [
    {
      id: '01',
      title: 'Décrivez votre problème',
      description: 'WhatsApp ou espace client : vous expliquez votre souci en 2 minutes, diagnostic gratuit.',
      icon: MessageSquare,
      delay: 0,
    },
    {
      id: '02',
      title: 'Un expert vous répond',
      description: "Un technicien vous recontacte sous 48h avec un premier plan d'action clair.",
      icon: Clock,
      delay: 0.08,
    },
    {
      id: '03',
      title: "Choix du mode d'intervention",
      description: 'Vous choisissez ensemble la meilleure option : dépannage à distance immédiat ou intervention sur place.',
      icon: Route,
      delay: 0.16,
    },
    {
      id: '04',
      title: 'Problème résolu',
      description: 'Vous payez uniquement si le problème est résolu, avec garantie 90 jours incluse.',
      icon: CheckCircle,
      delay: 0.24,
    },
  ] as const;

  const revealUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
  };

  const proOffers = [
    {
      title: 'Intervention ponctuelle',
      description: "Pour une panne urgente ou un besoin ponctuel sur site ou à distance.",
      badge: 'Rapide',
      icon: Wrench,
      accent: 'from-[#5865F2]/20 to-transparent',
    },
    {
      title: 'Suivi mensuel',
      description: 'Un accompagnement régulier pour stabiliser votre parc et prévenir les incidents.',
      badge: 'Le plus choisi',
      icon: ServerCog,
      accent: 'from-cyan-400/20 to-transparent',
    },
    {
      title: 'Audit & sécurisation',
      description: 'Analyse de votre infrastructure, recommandations claires et plan de priorités.',
      badge: 'Stratégique',
      icon: ShieldCheck,
      accent: 'from-emerald-400/20 to-transparent',
    },
  ] as const;

  const proNeeds = ['Pannes urgentes', 'Réseau lent', 'Microsoft 365', 'Sauvegardes', 'Sécurité', 'Support utilisateurs'] as const;

  return (
    <div className="pt-16 md:pt-20">

      {/* Hero */}
      <div className="relative py-20 min-h-[420px] flex items-center overflow-hidden bg-[#2F3136]">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/depan-hero.png"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#2F3136]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            variants={revealUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <motion.p
              className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/40 bg-[#5865F2]/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#B9C4FF] mb-4"
              animate={{ boxShadow: ['0 0 0 rgba(88,101,242,0)', '0 0 24px rgba(88,101,242,0.25)', '0 0 0 rgba(88,101,242,0)'] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            >
              Diagnostic gratuit en 2 minutes
            </motion.p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Panne informatique ? <span className="text-[#5865F2]">On vous dépanne aujourd'hui</span>
            </h1>
            <p className="text-gray-200 text-base md:text-lg max-w-3xl mx-auto mb-8">
              Particulier ou entreprise : décrivez votre problème en quelques messages et un technicien prend le relais à distance partout en France, ou sur place autour de Genève.
            </p>

            {/* Badges réassurance */}
            <motion.div
              className="flex flex-wrap items-center justify-center gap-3 mb-10"
              variants={revealUp}
              transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
            >
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <Zap className="h-4 w-4 text-[#5865F2]" /> Réponse sous 48h
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <Search className="h-4 w-4 text-[#5865F2]" /> Diagnostic gratuit
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <Shield className="h-4 w-4 text-[#5865F2]" /> Payez uniquement si résolu
              </span>
              <span className="flex items-center gap-1.5 bg-white/12 border border-white/20 rounded-full px-4 py-1.5 text-sm text-gray-100">
                <BadgeCheck className="h-4 w-4 text-[#5865F2]" /> 50% crédit d'impôt
              </span>
            </motion.div>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              variants={revealUp}
              transition={{ duration: 0.5, delay: 0.16, ease: 'easeOut' }}
            >
              <motion.a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Je veux être dépanné maintenant <ArrowRight className="ml-2 h-4 w-4" />
              </motion.a>
              <motion.a
                href="#particuliers"
                className="inline-flex items-center px-6 py-3 bg-white/5 border border-white/10 text-white font-medium rounded-lg hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Voir les tarifs clairement
              </motion.a>
            </motion.div>

          </motion.div>
        </div>
      </div>

      {/* Comment ça marche */}
      <section className="relative py-20 bg-[#36393F] overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <motion.div
            className="absolute -top-24 left-[10%] h-56 w-56 rounded-full bg-[#5865F2]/20 blur-3xl"
            animate={{ x: [0, 20, -8, 0], y: [0, -12, 14, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-24 right-[6%] h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl"
            animate={{ x: [0, -24, 10, 0], y: [0, 12, -18, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/35 bg-[#5865F2]/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-[#9BA8FF] uppercase mb-4">
              Processus express
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Comment ça <span className="text-[#5865F2]">marche ?</span>
            </h2>
            <p className="text-gray-400 text-sm">Un parcours simple, clair et rapide en 4 étapes</p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:block absolute top-11 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-[#5865F2]/35 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {howItWorksSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="relative">
                    {/* Mobile vertical connector */}
                    {index < howItWorksSteps.length - 1 && (
                      <div className="md:hidden absolute -bottom-3 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-[#5865F2]/40 to-transparent z-10" />
                    )}
                  <motion.div
                    className="group relative text-center p-6 md:p-7 rounded-2xl border border-white/10 bg-gradient-to-b from-white/10 to-white/[0.03] backdrop-blur-sm"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.55, delay: step.delay, ease: 'easeOut' }}
                    whileHover={{ y: -6, scale: 1.01, transition: { duration: 0.22, ease: 'easeOut' } }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#5865F2]/20 via-transparent to-cyan-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      aria-hidden="true"
                    />

                    <div className="relative z-10">
                      <motion.div
                        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5865F2]/20 border border-[#5865F2]/35 text-[#9BA8FF]"
                        whileHover={{ rotate: [0, -5, 5, 0] }}
                        transition={{ duration: 0.45 }}
                      >
                        <Icon className="h-6 w-6" />
                      </motion.div>

                      <span className="inline-flex rounded-full border border-[#5865F2]/30 bg-[#5865F2]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9BA8FF] mb-3">
                        Étape {step.id}
                      </span>

                      <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                      <p className="text-gray-300/90 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#5865F2]/25 to-transparent" />

      {/* Section particuliers */}
      <section id="particuliers" className="py-16 bg-[#2F3136]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex items-center mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <User className="h-6 w-6 text-[#5865F2] mr-3" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Pour les <span className="text-[#5865F2]">particuliers</span>
            </h2>
          </motion.div>
          <motion.p
            className="text-gray-300 max-w-3xl mb-8 md:whitespace-nowrap"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, delay: 0.05, ease: 'easeOut' }}
          >
            En 10 secondes, décrivez votre besoin : le technicien définit avec vous le mode d'intervention le plus adapté, à distance ou à domicile.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <motion.div
              className="bg-gradient-to-b from-white/10 to-white/[0.03] rounded-xl p-6 border border-white/15 cursor-pointer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.01 }}
            >
              <div className="flex items-center mb-3">
                <div className="bg-[#5865F2]/20 rounded-full p-2 mr-3 border border-[#5865F2]/35">
                  <Wifi className="h-5 w-5 text-[#5865F2]" />
                </div>
                <h3 className="text-white font-semibold">Dépannage à distance</h3>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-4">
                <MapPin className="h-3 w-3 flex-shrink-0" /> Partout en France
              </div>
              <p className="text-gray-300 text-sm mb-5">
                Prise en main sécurisée de votre ordinateur, sans déplacement. Idéal pour un dépannage rapide, avec paiement uniquement si le problème est résolu.
              </p>
              <div className="rounded-lg border border-[#5865F2]/30 bg-[#5865F2]/10 p-4">
                <p className="text-[#AEB9FF] text-xs uppercase tracking-wide mb-2">Tarifs à distance</p>
                <div className="space-y-1">
                  <p className="text-[#D2D9FF] font-semibold">Intervention simple (30 min) : 50€</p>
                  <p className="text-gray-300 text-sm">Intervention complexe (60 min) : 80€</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="bg-gradient-to-b from-white/10 to-white/[0.03] rounded-xl p-6 border border-white/15 cursor-pointer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, delay: 0.08, ease: 'easeOut' }}
              whileHover={{ y: -6, scale: 1.01 }}
            >
              <div className="flex items-center mb-3">
                <div className="bg-[#5865F2]/20 rounded-full p-2 mr-3 border border-[#5865F2]/35">
                  <Home className="h-5 w-5 text-[#5865F2]" />
                </div>
                <h3 className="text-white font-semibold">Intervention à domicile</h3>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-4">
                <MapPin className="h-3 w-3 flex-shrink-0" /> 50 km autour de Genève · Ain (01) · Haute-Savoie (74)
              </div>
              <p className="text-gray-300 text-sm mb-5">
                Un expert se déplace chez vous avec déplacement inclus, jusqu'à 1h d'intervention, et possibilité d'intervention le jour même selon disponibilités.
              </p>
              <div className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 p-4">
                <p className="text-cyan-200 text-xs uppercase tracking-wide mb-2">Tarif à domicile</p>
                <p className="text-white font-semibold">
                  150€ soit 75€ <span className="text-gray-300 font-normal text-xs">après crédit d'impôt (déplacement inclus)</span>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />

      {/* Section TPE/PME */}
      <section id="tpe-pme" className="py-16 bg-[#36393F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex items-center mb-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <Users className="h-6 w-6 text-[#5865F2] mr-3" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Pour les <span className="text-[#5865F2]">TPE/PME</span>
            </h2>
          </motion.div>
          <motion.p
            className="text-gray-300 max-w-3xl mb-6 md:whitespace-nowrap"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, delay: 0.05, ease: 'easeOut' }}
          >
            Un support informatique pro pour limiter les interruptions d'activité, sécuriser votre environnement et vous redonner de la visibilité sur vos priorités IT.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-2 mb-8"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, delay: 0.1, ease: 'easeOut' }}
          >
            {proNeeds.map((need) => (
              <span key={need} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-gray-200">
                {need}
              </span>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
            {proOffers.map((offer, index) => {
              const Icon = offer.icon;
              return (
                <motion.div
                  key={offer.title}
                  className="group relative overflow-hidden rounded-xl border border-white/12 bg-gradient-to-b from-white/10 to-white/[0.03] p-5 cursor-pointer"
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.45, delay: index * 0.07, ease: 'easeOut' }}
                  whileHover={{ y: -6, scale: 1.01 }}
                >
                  <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${offer.accent} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                  <div className="relative">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 bg-white/10 text-[#B9C4FF]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] uppercase tracking-wide text-gray-200">
                        {offer.badge}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold mb-2">{offer.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{offer.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="bg-white/5 rounded-xl p-6 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-4"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div>
              <p className="text-white font-semibold mb-1">Parlons de vos besoins IT en 15 minutes</p>
              <p className="text-gray-300 text-sm">Audit express, priorités claires, plan d'action concret et proposition adaptée à votre entreprise.</p>
            </div>
            <div className="flex">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-5 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300 whitespace-nowrap"
              >
                <PhoneCall className="mr-2 h-4 w-4" />
                Planifier un audit 15 min <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Services />

      <Faq showLink={false} showAllQuestions={true} />

      {/* Zones d'intervention locales */}
      <section className="py-14 bg-[#2F3136] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-3"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            Intervention à domicile dans le <span className="text-[#5865F2]">Genevois</span>
          </motion.h2>
          <p className="text-gray-400 text-center mb-8 max-w-xl mx-auto">
            En plus du dépannage à distance partout en France, nous intervenons à domicile dans les villes suivantes.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {[
              { label: 'Dépannage informatique dans le Genevois', href: '/depannage-informatique-genevois' },
              { label: 'Dépannage informatique à Saint-Julien-en-Genevois', href: '/depannage-informatique-saint-julien-en-genevois' },
              { label: 'Dépannage informatique à Viry', href: '/depannage-informatique-viry' },
              { label: 'Dépannage informatique à Valleiry', href: '/depannage-informatique-valleiry' },
              { label: 'Dépannage informatique à Annemasse', href: '/depannage-informatique-annemasse' },
              { label: 'Dépannage informatique à Annecy', href: '/depannage-informatique-annecy' },
              { label: 'Dépannage informatique à Gex', href: '/depannage-informatique-gex' },
            ].map(({ label, href }) => (
              <RouterLink
                key={href}
                to={href}
                className="flex items-center gap-2 bg-white/5 hover:bg-[#5865F2]/20 border border-white/10 hover:border-[#5865F2]/50 text-gray-300 hover:text-white px-4 py-2 rounded-full text-sm transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 text-[#5865F2]" /> {label}
              </RouterLink>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-14 bg-[#36393F] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="rounded-2xl border border-[#5865F2]/35 bg-gradient-to-r from-[#5865F2]/20 via-[#5865F2]/10 to-cyan-400/10 p-7 md:p-8 text-center"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p className="text-[#C7D0FF] text-xs uppercase tracking-wider mb-3">Pret a regler le probleme aujourd'hui ?</p>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
              Décrivez votre panne en 2 minutes, on vous répond sous 48h
            </h3>
            <p className="text-gray-200 text-sm md:text-base mb-6">
              Diagnostic gratuit, intervention a distance partout en France ou sur place autour de Geneve.
            </p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
            >
              Je veux être dépanné maintenant <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DepannageInformatique;
